import React from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  LayoutGrid,
  Download,
} from 'lucide-react';
import { ContentStatus, ContentSummary } from '../../types/content';
import { cn } from '../../utils/helpers';

interface ContentSummaryCardsProps {
  summary: ContentSummary | null;
  loading: boolean;
  activeStatus?: ContentStatus | 'ALL';
  onStatusClick: (status: ContentStatus | 'ALL') => void;
}

interface CardConfig {
  label: string;
  key: keyof ContentSummary;
  status: ContentStatus | 'ALL';
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

const cards: CardConfig[] = [
  {
    label: 'Pending Review',
    key: 'pendingReview',
    status: ContentStatus.PENDING_REVIEW,
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10',
  },
  {
    label: 'Pending Import',
    key: 'pendingImport',
    status: ContentStatus.PENDING_IMPORT,
    icon: Download,
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
  },
  {
    label: 'Ready',
    key: 'ready',
    status: ContentStatus.READY,
    icon: CheckCircle,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/10',
  },
  {
    label: 'Rejected',
    key: 'rejected',
    status: ContentStatus.REJECTED,
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
  },
  {
    label: 'Processing',
    key: 'processing',
    status: ContentStatus.PROCESSING,
    icon: Loader2,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
  },
  {
    label: 'Failed',
    key: 'failed',
    status: ContentStatus.FAILED,
    icon: AlertTriangle,
    color: 'text-orange-400',
    bgColor: 'bg-orange-400/10',
  },
  {
    label: 'Total',
    key: 'total',
    status: 'ALL',
    icon: LayoutGrid,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
  },
];

const ContentSummaryCards: React.FC<ContentSummaryCardsProps> = ({
  summary,
  loading,
  activeStatus,
  onStatusClick,
}) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        const count = summary ? summary[card.key] : 0;
        const isActive = activeStatus === card.status;

        return (
          <button
            key={card.key}
            onClick={() => onStatusClick(card.status)}
            className={cn(
              'flex items-center gap-3 p-3 rounded-xl border transition-all duration-200',
              'hover:shadow-glow-sm cursor-pointer text-left',
              isActive
                ? 'border-primary/40 bg-primary/5 shadow-glow-sm'
                : 'border-border bg-card hover:border-primary/20'
            )}
          >
            <div className={cn('p-2 rounded-lg', card.bgColor)}>
              <Icon className={cn('h-4 w-4', card.color)} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate">{card.label}</p>
              {loading ? (
                <div className="h-6 w-8 bg-muted/30 rounded animate-pulse mt-0.5" />
              ) : (
                <p className="text-lg font-bold text-foreground">{count}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ContentSummaryCards;
